import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { useCountdown } from '@renderer/utils/countdownUtils'
import { unifiedCameraUtils } from '@renderer/utils/cameras/uniCamUtils'
import Preview from '@renderer/components/Preview'
import { flashEffect } from '@renderer/utils/flashUtils'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { useTranslation } from 'react-i18next'
import { useSharedCountdown } from './hooks/useSharedCountdown'
import { filterUtils } from '@renderer/utils/filterUtils'
import { applyLUTThenOverlay, applyFilterOverlayImage} from '@renderer/utils/lutUtils'
import { FaUndo } from 'react-icons/fa'
import cogoToast from '@successtar/cogo-toast'
import PrintPreview, { PrintPreviewRef } from '@renderer/components/PrintPreview'
import { Recorder, RecorderStatus } from 'canvas-record'
import { AVC } from 'media-codecs'
import { getNormalizedCaptureRatio as getSharedNormalizedCaptureRatio } from '@renderer/utils/captureRatioUtils'
import FrameCanvasBackdrop from '@renderer/components/FrameCanvasBackdrop'

const RetakePage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { setSessionState, userPreferences, id, gallery, uploadSessionState, logActivity } =
    useSessionStateStore()
  const { config: globalConfig, filters, getFrame, getPageSettings } = useConfigStore()
  const config = globalConfig?.pages.capture
  const captureMode = config?.settings.mode || 'standard'
  const { downloadLocal, cameraSettings } = useSettingsStore()

  // Get selected filter from user preferences, fallback to first filter
  const selectedFilterId = userPreferences.filterId
  const selectedFilter = selectedFilterId
    ? filters.find((f) => f._id === selectedFilterId) || filters[0]
    : filters[0]
  const filterAdjustments = selectedFilter?.adjustments

  // Get selected frame
  const selectedFrame = getFrame(userPreferences.frameId || '')
  if (!selectedFrame) {
    navigate(`/${projectId}/frame`)
    return null
  }

  // Get unique photo types from frame (like Capture.tsx)
  const uniquePhotoTypes = new Set(
    selectedFrame.drawParams.images
      .map((image) => image.type)
      .filter((type) => type.startsWith('photo-'))
  )
  const uniquePhotoTypesArray = Array.from(uniquePhotoTypes).sort()

  // Get all photo slots for display
  const photoSlots = selectedFrame.drawParams.images.filter((img) => img.type.startsWith('photo-'))
  const capturedItems = gallery.capturedItems || []

  // Build map of photos by type from captured items
  // Use the FIRST N photos (where N = number of unique photo types) - matches Processing.tsx logic
  // Processing.tsx uses .slice(0, uniquePhotoTypesArray.length) to get first N photos
  // Since we update items in place (keeping same IDs), order is preserved and first N is correct
  const capturedPhotosByType = new Map<string, string>()
  const photosOnly = capturedItems.filter(
    (item) =>
      item.photoUrl &&
      !item.id?.includes('dreambooth-strip') &&
      !item.id?.includes('dreambooth-gif')
  )
  const firstNPhotos = photosOnly.slice(0, uniquePhotoTypesArray.length)

  firstNPhotos.forEach((item, index) => {
    if (index < uniquePhotoTypesArray.length) {
      const photoType = uniquePhotoTypesArray[index]
      capturedPhotosByType.set(photoType, item.photoUrl || '')
    }
  })

  // Shared countdown
  const { remainingTime, isExpired } = useSharedCountdown()

  // Retake state - track by photo type
  const [retakingPhotoType, setRetakingPhotoType] = useState<string | null>(null)
  const [capturing, setCapturing] = useState(false)
  const [captureCountdown, setCaptureCountdown] = useState<number>(
    config?.settings.captureCountdown || 3
  )
  const [isCaptureError, setIsCaptureError] = useState(false)
  // Use a separate state that forces re-render when countdown starts
  const [countdownKey, setCountdownKey] = useState(0)
  const [canvasDimensions, setCanvasDimensions] = useState<{
    width: number
    height: number
  } | null>(null)

  const captureCountdownHook = useCountdown(config?.settings.captureCountdown || 3, { audioCue: true })
  const captureLockRef = useRef<boolean>(false)
  const previewRef = useRef<PrintPreviewRef>(null)
  // Ref to store current retakingPhotoType so onComplete callback can access latest value
  const retakingPhotoTypeRef = useRef<string | null>(null)

  // Video recording setup
  const useRecording = userPreferences.useRecording
  const targetDuration = (config?.settings.captureCountdown || 3) * 1000
  const FPS = 30
  const totalFrames = Math.ceil((targetDuration * FPS) / 1000)
  const retakeCanvasRecorderRef = useRef<Recorder | null>(null)
  const recordingActiveRef = useRef<boolean>(false)
  const recordingPromiseRef = useRef<Promise<void> | null>(null)

  // Calculate scale for fullscreen frame display using actual screenSize
  const screenWidth = globalConfig?.screenSize.width || 1080
  const screenHeight = globalConfig?.screenSize.height || 1920
  const frameWidth = selectedFrame.drawParams.canvasWidth
  const frameHeight = selectedFrame.drawParams.canvasHeight

  // Scale frame to fit screen while maintaining aspect ratio
  // Account for padding, top gap, and bottom whitespace for buttons (countdown + continue button)
  const paddingSize = Math.max(screenWidth * 0.015, 16) // 1.5% of width, min 16px
  const topGap = Math.max(screenHeight * 0.05, 80) // 5% of height, min 80px for top gap
  const buttonAreaHeight = Math.max(screenHeight * 0.15, 180) // 15% of height, min 180px for button area
  const availableWidth = screenWidth - paddingSize * 2
  const availableHeight = screenHeight - paddingSize * 2 - topGap - buttonAreaHeight
  const scale = Math.min(availableWidth / frameWidth, availableHeight / frameHeight)

  const getNormalizedCaptureRatio = (): number | undefined =>
    getSharedNormalizedCaptureRatio(cameraSettings.captureRatio)

  // Get photo ratio from frame
  const getPhotoRatio = (slot: (typeof photoSlots)[0]): number => {
    return slot.width / slot.height
  }

  // Helper function to ensure even dimensions for H.264 codec
  const validateDimensions = (width: number, height: number): { width: number; height: number } => {
    // Round down to nearest even number
    const validatedWidth = Math.floor(width / 2) * 2
    const validatedHeight = Math.floor(height / 2) * 2
    return { width: validatedWidth, height: validatedHeight }
  }

  // Initialize recorder when canvas is ready
  // NOTE: This useEffect is kept for compatibility but recorder is actually created in handleRetakePhoto
  // The useEffect will only create a recorder if handleRetakePhoto hasn't already created one
  useEffect(() => {
    // Don't create recorder in useEffect if one already exists (handleRetakePhoto creates it)
    if (retakeCanvasRecorderRef.current) {
      return
    }

    if (canvasDimensions && retakingPhotoType) {
      const canvas = document.querySelector<HTMLCanvasElement>('canvas[data-preview-type="camera"]') ?? document.querySelector('canvas')
      if (!canvas) return

      if (
        useRecording &&
        (userPreferences.recordingType === 'strip_live' ||
          userPreferences.recordingType === 'strip_story')
      ) {
        // H.264 requires even dimensions - validate and specify in encoderOptions
        // Don't resize the canvas itself (it's used by Preview component)
        const validatedDims = validateDimensions(canvasDimensions.width, canvasDimensions.height)
        
        // Get context (don't modify canvas)
        const canvasContext = canvas.getContext('bitmaprenderer')
        if (!canvasContext) {
          // Don't log error here - handleRetakePhoto will handle recorder creation
          return
        }

        console.log(
          `[Retake] Creating recorder in useEffect with validated dimensions: ${validatedDims.width}x${validatedDims.height} (original: ${canvasDimensions.width}x${canvasDimensions.height})`
        )

        try {
          const newRetakeRecorder = new Recorder(canvasContext as RenderingContext, {
            name: `dreambooth-${id}-retake`,
            encoderOptions: {
              codec: AVC.getCodec({ profile: 'Baseline', level: '4.1' }),
              width: validatedDims.width,
              height: validatedDims.height,
              bitrate: 2000000
            },
            duration: Infinity,
            download:
              downloadLocal &&
              (userPreferences.recordingType === 'strip_live' ||
                userPreferences.recordingType === 'strip_story'),
            frameRate: FPS
          })
          retakeCanvasRecorderRef.current = newRetakeRecorder
          console.log(`[Retake] Recorder created successfully in useEffect with encoder dimensions: ${validatedDims.width}x${validatedDims.height}`)
        } catch (error) {
          const errorMsg = error instanceof Error ? error.message : String(error)
          console.error('[Retake] Error creating recorder in useEffect:', errorMsg, error)
          // Continue without recording if recorder creation fails
          retakeCanvasRecorderRef.current = null
        }
      }
    }

    return (): void => {
      // Only stop recorder on unmount, not when dependencies change
      // This prevents stopping a recorder that's actively being used
    }
  }, [canvasDimensions, retakingPhotoType, useRecording, userPreferences.recordingType, downloadLocal, id, FPS])

  // Imperative frame recording loop (no useEffect race). Called from handleRetakePhoto after recorder.start().
  const runFrameRecordingLoop = async (photoType: string): Promise<void> => {
    if (!useRecording || !retakeCanvasRecorderRef.current) return
    if (retakeCanvasRecorderRef.current.status !== RecorderStatus.Recording) return
    if (recordingActiveRef.current) return
    recordingActiveRef.current = true
    let frameCount = 0
    const startTime = performance.now()
    console.log(`[Retake] Starting frame recording for photo type ${photoType}, target frames: ${totalFrames}`)
    while (
      recordingActiveRef.current &&
      frameCount < totalFrames &&
      retakeCanvasRecorderRef.current?.status === RecorderStatus.Recording
    ) {
      const elapsedTime = performance.now() - startTime
      const targetFrameTime = (frameCount * 1000) / FPS
      if (elapsedTime >= targetFrameTime) {
        try {
          await retakeCanvasRecorderRef.current.step()
          frameCount++
        } catch (error) {
          console.error(`[Retake] Error stepping retake recorder for photo type ${photoType}:`, error)
        }
      }
      await new Promise((r) => setTimeout(r, 1))
    }
    recordingActiveRef.current = false
    console.log(`[Retake] Finished recording frames for photo type ${photoType}: ${frameCount}/${totalFrames} frames recorded`)
  }

  // Navigate away if countdown expired
  useEffect(() => {
    if (isExpired) {
      logActivity('Shared countdown expired on Retake page')
      // Navigate to Select to let it handle processing automatically
      navigate(`/${projectId}/select`)
    }
  }, [isExpired])

  // Set up capture countdown callbacks (run once on mount, not on retakingPhotoType change)
  useEffect(() => {
    captureCountdownHook.setOnTick(() => {
      setCaptureCountdown(captureCountdownHook.getRemainingSeconds())
    })

    captureCountdownHook.setOnComplete(() => {
      handleRetakeCapture()
    })

    return (): void => {
      // Don't stop on cleanup - let handleRetakePhoto manage starting/stopping
    }
  }, [])

  // Keep ref in sync with state
  useEffect(() => {
    retakingPhotoTypeRef.current = retakingPhotoType
  }, [retakingPhotoType])

  // Sync countdown state when capturing becomes true - force immediate sync
  useEffect(() => {
    if (capturing) {
      const currentRemaining = captureCountdownHook.getRemainingSeconds()
      // Force sync to hook's value when capturing starts - ensure it matches
      if (currentRemaining > 0 && currentRemaining !== captureCountdown) {
        setCaptureCountdown(currentRemaining)
      }
    }
  }, [capturing])

  const handleRetakePhoto = async (photoType: string): Promise<void> => {
    if (capturing || isExpired) return

    logActivity(`Starting retake for photo type ${photoType}`)
    setRetakingPhotoType(photoType)
    setIsCaptureError(false)

    // Wait for React to re-render and Preview component to mount before looking for canvas
    await new Promise((resolve) => setTimeout(resolve, 200))

    // Start capture recording if enabled
    // CRITICAL: Must start recorder BEFORE starting countdown so frames are recorded during countdown
    if (useRecording) {
      try {
        // Wait for Preview component's canvas (data-preview-type="camera"); avoid PrintPreview's canvas (data-preview-type="print")
        let canvas: HTMLCanvasElement | null = null
        let retries = 0
        const maxRetries = 10

        while (!canvas && retries < maxRetries) {
          canvas = document.querySelector<HTMLCanvasElement>('canvas[data-preview-type="camera"]')

          if (!canvas) {
            retries++
            if (retries < maxRetries) {
              console.log(`[Retake] Preview canvas not found for photo type ${photoType}, retrying... (${retries}/${maxRetries})`)
              await new Promise((resolve) => setTimeout(resolve, 100))
            }
          } else {
            console.log(`[Retake] Preview canvas found for photo type ${photoType} after ${retries} retries`, {
              width: canvas.width,
              height: canvas.height
            })
          }
        }

        if (canvas) {
          // Use canvas element dimensions (source of truth); avoid stale canvasDimensions state
          let canvasWidth = canvas.width
          let canvasHeight = canvas.height
          if (!canvasWidth || !canvasHeight) {
            for (let w = 0; w < 5; w++) {
              await new Promise((r) => setTimeout(r, 50))
              canvasWidth = canvas.width
              canvasHeight = canvas.height
              if (canvasWidth && canvasHeight) break
            }
          }
          if (canvasWidth && canvasHeight) {
            // CRITICAL: Retry getting context - Preview component might not have initialized it yet
            let canvasContext: RenderingContext | null = null
            let contextRetries = 0
            const maxContextRetries = 10
            
            while (!canvasContext && contextRetries < maxContextRetries) {
              canvasContext = canvas.getContext('bitmaprenderer')
              if (!canvasContext) {
                contextRetries++
                if (contextRetries < maxContextRetries) {
                  console.log(`[Retake] Context not available for photo type ${photoType}, retrying... (${contextRetries}/${maxContextRetries})`)
                  await new Promise((resolve) => setTimeout(resolve, 100))
                }
              } else {
                console.log(`[Retake] Context obtained for photo type ${photoType} after ${contextRetries} retries`)
              }
            }
            
            if (!canvasContext) {
              console.error(`[Retake] Failed to get bitmaprenderer context for photo type ${photoType} after ${maxContextRetries} retries`)
              console.error(`[Retake] Canvas element:`, canvas)
              console.error(`[Retake] Canvas width: ${canvas.width}, height: ${canvas.height}`)
              // Continue without recording if context is not available
            } else {
              const validatedDims = validateDimensions(canvasWidth, canvasHeight)
              
              // Clean up old recorder completely
              if (retakeCanvasRecorderRef.current) {
                try {
                  if (retakeCanvasRecorderRef.current.status === RecorderStatus.Recording) {
                    await retakeCanvasRecorderRef.current.stop()
                  }
                } catch (e) {
                  console.warn('[Retake] Error stopping old recorder:', e)
                }
                retakeCanvasRecorderRef.current = null
              }
              
              // Wait a moment to ensure old recorder is fully cleaned up
              await new Promise((resolve) => setTimeout(resolve, 100))
              
              // Create new recorder for retake
              console.log(`[Retake] Creating new recorder for photo type ${photoType}`, {
                dimensions: `${validatedDims.width}x${validatedDims.height}`,
                fps: FPS,
                canvasWidth: canvas.width,
                canvasHeight: canvas.height
              })
              
              try {
                const newRecorder = new Recorder(canvasContext as RenderingContext, {
                  name: `dreambooth-${id}-retake-${photoType}`,
                  encoderOptions: {
                    codec: AVC.getCodec({ profile: 'Baseline', level: '4.1' }),
                    width: validatedDims.width,
                    height: validatedDims.height,
                    bitrate: 2000000
                  },
                  duration: Infinity,
                  download:
                    downloadLocal &&
                    (userPreferences.recordingType === 'strip_live' ||
                      userPreferences.recordingType === 'strip_story'),
                  frameRate: FPS
                })
                retakeCanvasRecorderRef.current = newRecorder
                
                console.log(`[Retake] Created new recorder for photo type ${photoType}, initial status: ${retakeCanvasRecorderRef.current.status}`)
                
                // CRITICAL: Start the recorder BEFORE starting countdown
                // This ensures frames are recorded during the countdown period
                console.log(`[Retake] Starting recorder for photo type ${photoType}...`)
                await retakeCanvasRecorderRef.current.start()
                
                // Verify recorder is actually in Recording status before proceeding
                const recorderStatus = retakeCanvasRecorderRef.current.status
                console.log(`[Retake] Recorder start completed for photo type ${photoType}, status: ${recorderStatus}`)
                
                if (recorderStatus !== RecorderStatus.Recording) {
                  console.error(`[Retake] Recorder failed to start for photo type ${photoType}, status: ${recorderStatus}`, {
                    expectedStatus: RecorderStatus.Recording,
                    actualStatus: recorderStatus
                  })
                  retakeCanvasRecorderRef.current = null
                } else {
                  console.log(`[Retake] Successfully started recorder for photo type ${photoType}, status: ${recorderStatus}`)
                }
              } catch (recorderError) {
                const errorMsg = recorderError instanceof Error ? recorderError.message : String(recorderError)
                console.error(`[Retake] Error creating recorder for photo type ${photoType}:`, errorMsg, recorderError)
                retakeCanvasRecorderRef.current = null
              }
            }
          } else {
            console.error(`[Retake] Invalid canvas dimensions for photo type ${photoType}`, {
              canvasWidth,
              canvasHeight,
              canvasDimensions
            })
          }
        }
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : String(error)
        console.error(`[Retake] Error creating/starting recorder for photo type ${photoType}:`, errorMsg, error)
        retakeCanvasRecorderRef.current = null
      }
    }

    const captureTime = config?.settings.captureCountdown || 3
    // Stop countdown first to ensure clean state
    captureCountdownHook.stop()
    // Set countdown seconds
    captureCountdownHook.setRemainingSeconds(captureTime)
    // Set countdown state to the correct value
    setCaptureCountdown(captureTime)
    // Force a new key to ensure React remounts the countdown display element
    // This is critical - it forces React to create a new element with the new state value
    setCountdownKey((prev) => prev + 1)
    // Start the countdown hook
    // Autofocus now, while the countdown runs, so the shutter is not waiting on it (camera_prefocus).
    unifiedCameraUtils.prefocus()
    captureCountdownHook.start()
    setCapturing(true)
    // Imperative frame recording: start immediately (no useEffect race)
    if (useRecording && retakeCanvasRecorderRef.current?.status === RecorderStatus.Recording) {
      recordingPromiseRef.current = runFrameRecordingLoop(photoType)
    }
  }

  const handleRetakeCapture = async (): Promise<void> => {
    // Use ref to get latest value (callbacks may have stale closures)
    const currentPhotoType = retakingPhotoTypeRef.current
    if (captureLockRef.current || currentPhotoType === null) {
      return
    }

    captureLockRef.current = true

    try {
      console.log('Starting retake capture for photo type', currentPhotoType)

      flashEffect('.flash')
      
      // CRITICAL: Stop frame recording BEFORE stopping the recorder
      recordingActiveRef.current = false
      if (recordingPromiseRef.current) {
        await recordingPromiseRef.current
      }
      recordingPromiseRef.current = null
      
      setCapturing(false)
      // Reset countdown state when capture completes to prevent stale value on next retake
      const captureTime = config?.settings.captureCountdown || 3
      setCaptureCountdown(captureTime)

      await new Promise((resolve) => setTimeout(resolve, 200))

      // Stop and save retake recording if enabled
      let retakeVideoUrl: string | undefined = undefined
      if (useRecording && retakeCanvasRecorderRef.current) {
        try {
          const recorderStatus = retakeCanvasRecorderRef.current.status
          console.log(`[Retake] Stopping recorder for photo type ${currentPhotoType}, current status: ${recorderStatus}`)
          
          // Check if recorder was actually recording before trying to stop
          if (recorderStatus === RecorderStatus.Recording) {
            const retakeChunks = await retakeCanvasRecorderRef.current.stop()
            // Handle different return types: ArrayBuffer | Blob[] | Uint8Array
            const chunksLength = Array.isArray(retakeChunks) 
              ? retakeChunks.length 
              : retakeChunks instanceof ArrayBuffer 
                ? retakeChunks.byteLength 
                : retakeChunks instanceof Uint8Array 
                  ? retakeChunks.length 
                  : 0
            console.log(`[Retake] Recorder stopped for photo type ${currentPhotoType}, chunks length: ${chunksLength}`)
            
            if (retakeChunks && chunksLength > 0) {
              const retakeBlob = new Blob([retakeChunks as ArrayBuffer], { type: 'video/mp4' })
              console.log(`[Retake] Created blob for photo type ${currentPhotoType}, size: ${retakeBlob.size} bytes`)
              
              // Validate blob size - empty blobs indicate recording failed
              if (retakeBlob.size > 1000) { // Minimum 1KB to ensure it's a valid video
                retakeVideoUrl = URL.createObjectURL(retakeBlob)
                console.log(`[Retake] Created video blob URL for photo type ${currentPhotoType}: ${retakeVideoUrl}, size: ${retakeBlob.size} bytes`)
              } else {
                console.warn(`[Retake] Video blob is too small for photo type ${currentPhotoType} (${retakeBlob.size} bytes), recording may have failed`)
              }
            } else {
              console.warn(`[Retake] No video chunks recorded for photo type ${currentPhotoType}, recording may have failed`)
            }
          } else {
            console.warn(`[Retake] Recorder was not recording for photo type ${currentPhotoType} (status: ${recorderStatus}), skipping video creation. Recorder may have failed to start.`)
          }
        } catch (error) {
          const errorMsg = error instanceof Error ? error.message : String(error)
          console.error(`[Retake] Error stopping retake recorder for photo type ${currentPhotoType}:`, errorMsg, error)
        }
      } else if (useRecording) {
        console.warn(`[Retake] Recording enabled but recorder not available when stopping photo type ${currentPhotoType}`)
      }

      const imageData = await unifiedCameraUtils.captureImage(
        userPreferences.selectedCamera as 'primary' | 'secondary',
        applyFilter,
        getNormalizedCaptureRatio(),
        !cameraSettings.useShutterCapture
      )

      if (imageData) {
        console.log('Successfully retook image')
        const imageUrl = URL.createObjectURL(imageData)

        if (downloadLocal) {
          const a = document.createElement('a')
          a.href = imageUrl
          a.download = `dreambooth-${id}-retake-${Date.now()}.jpg`
          a.setAttribute('data-automated-click', 'true')
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
        }

        // Get the LATEST capturedItems from store (not closure) to avoid stale state
        // This ensures we have the most recent updates from previous retakes
        const latestCapturedItems = useSessionStateStore.getState().gallery.capturedItems || []

        // Find the gallery item that corresponds to this photo type
        // Use the same logic as capturedPhotosByType: first N photos mapped to photo types
        const photosOnly = latestCapturedItems.filter(
          (item) =>
            item.photoUrl &&
            !item.id?.includes('dreambooth-strip') &&
            !item.id?.includes('dreambooth-gif')
        )
        const firstNPhotos = photosOnly.slice(0, uniquePhotoTypesArray.length)
        const photoTypeIndex = uniquePhotoTypesArray.indexOf(currentPhotoType)
        const updatedItems = [...latestCapturedItems]

        // Find the actual gallery item index for this photo type
        let galleryItemIndex = -1
        if (photoTypeIndex >= 0 && photoTypeIndex < firstNPhotos.length) {
          // Find the index of this item in the full latestCapturedItems array using ID
          const targetItem = firstNPhotos[photoTypeIndex]
          galleryItemIndex = latestCapturedItems.findIndex((item) => item.id === targetItem.id)
        }

        // Update the gallery item at the found index
        if (galleryItemIndex >= 0 && galleryItemIndex < updatedItems.length) {
          // Get original item
          const originalItem = updatedItems[galleryItemIndex]
          const originalRecordingUrl = originalItem.recordingUrl || ''
          
          // Replace with new photo and NEW video (if recording was successful)
          // CRITICAL: Use the new retake video if available, otherwise preserve original
          const newRecordingUrl = retakeVideoUrl || originalRecordingUrl
          
          updatedItems[galleryItemIndex] = {
            ...originalItem,
            photoUrl: imageUrl,
            recordingUrl: newRecordingUrl,
            timestamp: new Date()
          }

          console.log(`[Retake] Updated photo ${currentPhotoType}`, {
            photoType: currentPhotoType,
            photoTypeIndex,
            galleryItemIndex,
            hasOriginalRecordingUrl: !!originalRecordingUrl,
            hasNewRecordingUrl: !!retakeVideoUrl,
            finalRecordingUrl: newRecordingUrl ? 'YES' : 'NO',
            originalRecordingUrl,
            newRecordingUrl: retakeVideoUrl
          })

          // DO NOT revoke blob URLs during retakes - PrintPreview and other components may still be loading them
          // Blob URLs will be automatically cleaned up by the browser when the page unloads or when explicitly revoked on unmount
          // Revoking during retakes causes "ERR_FILE_NOT_FOUND" errors when PrintPreview tries to load images
        } else {
          // If item not found, add new item (shouldn't happen in normal flow)
          console.warn(`[Retake] Gallery item not found for photo type ${currentPhotoType}, adding new item`)
          updatedItems.push({
            id: `dreambooth-${id}-retake-${Date.now()}`,
            photoUrl: imageUrl,
            timestamp: new Date()
          })
        }

        setSessionState({
          gallery: {
            capturedItems: updatedItems
          }
        })

        logActivity(`Retook photo type ${currentPhotoType}`, {
          photoType: currentPhotoType,
          photoTypeIndex,
          galleryItemIndex
        })

        cogoToast.success(t('capture.retakeSuccess', 'Photo retaken successfully!'))

        // Reset retake state
        setRetakingPhotoType(null)
        setCapturing(false)
      } else {
        logActivity('Retake capture failed: No image data captured.', {
          level: 'error',
          photoType: currentPhotoType
        })
        setIsCaptureError(true)
      }
    } catch (error) {
      console.error('Retake capture failed:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      logActivity(`Retake capture failed: ${errorMessage}`, {
        error: errorMessage,
        level: 'error',
        photoType: currentPhotoType
      })
      setIsCaptureError(true)
    } finally {
      captureLockRef.current = false
    }
  }

  const applyFilter = (ctx: CanvasRenderingContext2D): void => {
    if (selectedFilter?.filterMode === 'lut') {
      applyLUTThenOverlay(ctx, selectedFilter)
    } else if (filterAdjustments) {
      filterUtils.applyAdjustments(ctx, filterAdjustments)
      applyFilterOverlayImage(ctx, selectedFilter?.overlay)
    }
  }

  // Get frame config for PrintPreview
  const getFrameConfig = (
    frame: ReturnType<typeof getFrame>
  ): {
    id: string
    baseImage: string
    drawParams: {
      canvasWidth: number
      canvasHeight: number
      backgroundColor: string
      images: Array<{
        type: string
        top: number
        left: number
        width: number
        height: number
        src?: string
        rotation?: number
      }>
    }
  } => {
    if (!frame)
      return {
        id: '',
        baseImage: '',
        drawParams: {
          canvasWidth: 1200,
          canvasHeight: 1800,
          backgroundColor: '#FFFFFF',
          images: []
        }
      }
    return {
      id: frame._id,
      baseImage: frame.thumbnail,
      drawParams: frame.drawParams
    }
  }

  const frameConfig = getFrameConfig(selectedFrame)

  // Get captured photos array for PrintPreview (matching Processing.tsx logic)
  const capturedPhotos = Array.from(capturedPhotosByType.values()).filter(
    (url) => url.trim() !== ''
  )

  const handleRetakeAll = (): void => {
    if (captureMode === 'frame-based') return

    // Clear existing photo items (but preserve strips and GIFs) before retaking all
    const currentGallery = useSessionStateStore.getState().gallery
    const itemsToKeep = currentGallery.capturedItems.filter(
      (item) =>
        item.id?.includes('dreambooth-strip') || item.id?.includes('dreambooth-gif')
    )
    
    setSessionState({
      gallery: {
        capturedItems: itemsToKeep
      },
      userPreferences: {
        isRetakeFromRetakePage: true
      }
    })
    uploadSessionState()
    navigate(`/${projectId}/capture`)
  }

  const handleContinue = async (): Promise<void> => {
    try {
      // Export photo strip before navigating
      if (previewRef.current && capturedPhotos.length > 0) {
        // Wait for PrintPreview to be ready
        try {
          await Promise.race([
            previewRef.current.waitForReady(),
            new Promise<never>((_, reject) =>
              setTimeout(() => reject(new Error('PrintPreview timeout')), 5000)
            )
          ])
        } catch (error) {
          console.warn('PrintPreview waitForReady timeout or error:', error)
          // Continue anyway - PrintPreview might still work
        }

        // Generate strip
        const stripBlob = await previewRef.current.exportImage()
        if (stripBlob) {
          const stripUrl = URL.createObjectURL(stripBlob)

          // Save to gallery state - remove any existing strips first
          const currentGallery = useSessionStateStore.getState().gallery
          // Remove any existing strip items to prevent duplicates
          const itemsWithoutStrips = currentGallery.capturedItems.filter(
            (item) => !item.id?.includes('dreambooth-strip')
          )
          setSessionState({
            gallery: {
              capturedItems: [
                ...itemsWithoutStrips,
                {
                  id: `dreambooth-strip-${id}`,
                  stripUrl: stripUrl,
                  photoUrl: '',
                  timestamp: new Date()
                }
              ]
            }
          })

          // Wait a bit for state to propagate
          await new Promise((resolve) => setTimeout(resolve, 200))
        }
      }
    } catch (error) {
      console.error('Error exporting strip in Retake:', error)
      // Continue navigation even if strip export fails
    }

    uploadSessionState()
    
    // Navigation logic matches view/Retake.tsx exactly
    const filterSettings = getPageSettings('filter')
    
    // Frame-based mode: check filter settings first
    if (captureMode === 'frame-based') {
      const applyAfterCapture = filterSettings?.applyAfterCapture || false
      const filterEnabled = filterSettings?.enabled

      // If applyAfterCapture is enabled and filter is enabled, navigate to filter
      if (applyAfterCapture && filterEnabled) {
        navigate(`/${projectId}/filter`)
        return
      }
      // Otherwise navigate to select page
      navigate(`/${projectId}/select`)
      return
    }

    // Standard mode: check filter settings
    const applyAfterCapture = filterSettings?.applyAfterCapture || false
    const filterEnabled = filterSettings?.enabled

    // If applyAfterCapture is enabled and filter is enabled, navigate to filter
    if (applyAfterCapture && filterEnabled) {
      navigate(`/${projectId}/filter`)
    } else {
      // Always navigate to select page
      navigate(`/${projectId}/select`)
    }
  }

  return (
    <div className="w-full h-full bg-newspaper-white font-newspaper text-[#1a1a1a] relative flex flex-col overflow-hidden p-4">
      <div id="flash" className="absolute inset-0 flash z-[6]"></div>

      {/* 
        PrintPreview for strip export - intentionally hidden from user view.
        It's positioned off-screen with proper dimensions so the canvas can render correctly.
        We only use it to generate the photo strip via exportImage(), not for display.
      */}
      <div
        style={{
          position: 'absolute',
          visibility: 'hidden',
          width: `${frameConfig.drawParams.canvasWidth}px`,
          height: `${frameConfig.drawParams.canvasHeight}px`,
          top: '-9999px',
          left: '-9999px'
        }}
      >
        <PrintPreview
          ref={previewRef}
          frameConfig={frameConfig}
          selectedPhotos={capturedPhotos}
          isInteractive={false}
          sessionId={id || ''}
        />
      </div>

      {/* Shared Countdown Display */}
      <div className="absolute top-8 right-8 z-50">
        <div className="border-2 border-double border-[#2a2a2a] bg-newspaper-white p-6 text-center">
          <div className="text-2xl text-newspaper-black mb-2 font-newspaper">Time Remaining</div>
          <div className="font-bold text-newspaper-black text-5xl font-newspaper">{remainingTime}</div>
        </div>
      </div>

      {/* Frame Preview Area - Takes available space */}
      <div className="flex-1 min-h-0 relative flex items-center justify-center pt-20">
        {/* Full Frame Display */}
        <div
          className="absolute flex items-center justify-center"
          style={{
            width: `${frameWidth * scale}px`,
            height: `${frameHeight * scale}px`
          }}
        >
          {/* Frame Background Image */}
          <FrameCanvasBackdrop frame={selectedFrame} scale={scale} />

          {/* Photo Placeholders with Retake Buttons */}
          {photoSlots.map((slot, index) => {
            const isRetaking = retakingPhotoType === slot.type
            const capturedPhotoUrl = capturedPhotosByType.get(slot.type)
            // Only show retake button on the first slot of each type to avoid duplicates
            const isFirstSlotOfType = photoSlots.findIndex((s) => s.type === slot.type) === index

            return (
              <div
                key={`${slot.type}-${index}`}
                className="absolute overflow-hidden"
                style={{
                  left: `${slot.left * scale}px`,
                  top: `${slot.top * scale}px`,
                  width: `${slot.width * scale}px`,
                  height: `${slot.height * scale}px`
                }}
              >
                {isRetaking ? (
                  <div className="w-full h-full relative overflow-hidden">
                    <Preview
                      adjustments={filterAdjustments}
                      ratio={getPhotoRatio(slot)}
                      cameraRatio={getNormalizedCaptureRatio()}
                      cameraKey={userPreferences.selectedCamera as 'primary' | 'secondary'}
                      filter={selectedFilter?._id}
                      postLUTAdjustments={selectedFilter?.postLUTAdjustments}
                      onCanvasReady={setCanvasDimensions}
                      fillContainer={true}
                    />
                  </div>
                ) : capturedPhotoUrl ? (
                  <>
                    <img
                      src={capturedPhotoUrl}
                      alt={`Photo ${slot.type}`}
                      className="w-full h-full object-cover"
                      crossOrigin="anonymous"
                    />
                    {/* Retake Button - only show on first slot of each type */}
                    {!capturing && !isExpired && isFirstSlotOfType && (
                      <button
                        onClick={() => handleRetakePhoto(slot.type)}
                        className="absolute top-2 right-2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors shadow-lg z-10"
                        aria-label={`Retake photo ${slot.type}`}
                      >
                        <FaUndo className="text-newspaper-black text-base" />
                      </button>
                    )}
                  </>
                ) : (
                  <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-400 text-2xl">
                      {uniquePhotoTypesArray.indexOf(slot.type) + 1}
                    </span>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Capture Countdown Overlay */}
      {capturing && (
        <div className="absolute inset-0 z-[4] flex items-center justify-center">
          <span
            key={`countdown-${countdownKey}`}
            className="text-newspaper-black animate-countdown text-[200px]"
          >
            {captureCountdown}
          </span>
        </div>
      )}

      {/* Capture Error Message */}
      {isCaptureError && (
        <div className="absolute inset-0 bg-black/80 z-[5] flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-4xl font-bold text-red-500 mb-4">
              {t('capture.captureFailed', 'Capture Failed')}
            </h2>
            <p className="text-xl text-white/80 mb-6">
              {t('capture.stayStillCapturing', 'Please stay still when capturing')}
            </p>
            <button
              type="button"
              onClick={() => {
                setIsCaptureError(false)
                if (retakingPhotoType !== null) {
                  handleRetakePhoto(retakingPhotoType)
                }
              }}
              className="border-2 border-double border-[#2a2a2a] font-serif font-medium transition-all duration-200 hover:opacity-90 cursor-pointer text-lg bg-[#1a1a1a] text-[#fafaf8] rounded-none shadow-none px-8 py-2.5"
            >
              {t('common.retry', 'Retry')}
            </button>
          </div>
        </div>
      )}

      {/* Button Area - Fixed height, no overlap */}
      <div className="flex-shrink-0 relative flex items-center justify-center h-44">
        {/* Action Buttons */}
        <div className="flex gap-6 z-10">
          {captureMode !== 'frame-based' && (
            <button
              type="button"
              onClick={handleRetakeAll}
              disabled={isExpired}
              className="border-2 border-double border-[#2a2a2a] font-newspaper font-bold transition-all duration-200 hover:opacity-90 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed text-4xl bg-[#1a1a1a] text-[#fafaf8] rounded-none shadow-none px-16 py-5 min-w-[320px] tracking-wider"
            >
              {t('retake.retakeAll', 'Retake All')}
            </button>
          )}
          <button
            type="button"
            onClick={handleContinue}
            disabled={isExpired}
            className="border-2 border-double border-[#2a2a2a] font-newspaper font-bold transition-all duration-200 hover:opacity-90 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed text-4xl bg-[#1a1a1a] text-[#fafaf8] rounded-none shadow-none px-16 py-5 min-w-[320px] tracking-wider"
          >
            {t('common.continue', 'Continue')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default RetakePage
